import { defaultCurrencyInputFormatter } from '../../../common/shared/locale';
import { CurrencyConfig, MoneyInput } from '@flexfront/ui/react';
import { YearInputComponent } from './year-input-component';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface MoneyInYearEditComponentProps {
  yearLabel: string;
  amountLabel: string;
  year: number;
  amount: number;
  stepValue?: number;
  hasError?: boolean;
  minYear?: number;
  maxYear?: number;
  itemConfirmLabel?: string;
  onRequiresUpdate?: (year: number, value: number) => void;
  onValidate?:(hasError: boolean) => void;
  currencyConfig?: CurrencyConfig;
  isYearValid?: boolean;
}

export function MoneyInYearEditComponent(props: MoneyInYearEditComponentProps) {
  const { year: initialYear, amount: initialAmount, minYear, maxYear, onValidate } = props;
  const [year, setYear] = useState<number>(props.year);
  const [amount, setAmount] = useState<number>(props.amount);
  const [isValidYear, setIsValidYear] = useState<boolean>(true);
  const onValidateRef = useRef(onValidate);
  const lastNotifiedValidationRef = useRef<boolean | undefined>(undefined);
  const isYearValidFromParent = props.isYearValid ?? true;
  const hasYearError = Boolean(props.hasError) || !isYearValidFromParent || !isValidYear;

  useEffect(() => {
    onValidateRef.current = onValidate;
  }, [onValidate]);

  const validateYearForAmount = useCallback((inputAmount: number, inputYear?: number) => {
    if (!Number.isFinite(inputAmount) || inputAmount <= 0) {
      return true;
    }

    if (inputYear == null || Number.isNaN(inputYear) || inputYear <= 0) {
      return false;
    }

    if (minYear != null && inputYear < minYear) {
      return false;
    }

    if (maxYear != null && inputYear > maxYear) {
      return false;
    }

    return true;
  }, [minYear, maxYear]);

  function updateYearValidation(inputAmount: number, inputYear?: number) {
    const isValid = validateYearForAmount(inputAmount, inputYear);
    setIsValidYear(isValid);

    if (lastNotifiedValidationRef.current !== isValid) {
      onValidateRef.current?.(isValid);
      lastNotifiedValidationRef.current = isValid;
    }

    return isValid;
  }

  function onYearChange(newYear: number) {
    setYear(newYear);
    updateYearValidation(amount, newYear);
  }

  function onAmountChange(newAmount: number) {
    setAmount(newAmount);
    updateYearValidation(newAmount, year);
  }

  function saveValue() {
    const isYearValid = updateYearValidation(amount, year);
    if (isYearValid && props.onRequiresUpdate) {
      props.onRequiresUpdate(year, amount);
    }
  }

  useEffect(() => {
    setYear(initialYear);
    setAmount(initialAmount);
    const isValid = validateYearForAmount(initialAmount, initialYear);
    setIsValidYear(isValid);
    if (lastNotifiedValidationRef.current !== isValid) {
      onValidateRef.current?.(isValid);
      lastNotifiedValidationRef.current = isValid;
    }
  }, [initialYear, initialAmount, validateYearForAmount]);

  return (
    <>
      <div className={`edit-container-field edit-container-field-label ${props.hasError || hasYearError ? 'money-input-label-error' : ''}`}>{props.yearLabel}</div>
      <YearInputComponent
        year={year}
        minYear={props.minYear}
        maxYear={props.maxYear}
        hasError={props.hasError || hasYearError}
        onYearChanged={onYearChange}
      />
      <div className="money-input-container">
        <div
          className={`money-input-label ${props.hasError ? 'money-input-label-error': ''}`}>
            {props.amountLabel}
        </div>
      </div>
      <MoneyInput
        inputNumber={amount}
        currencyConfig={props.currencyConfig}
        currencyFormatter={defaultCurrencyInputFormatter}
        showCurrencySymbol={true}
        groupedClassNameSuffix="border"
        showIncrementAndDecrement={true}
        stepValue={props.stepValue ? props.stepValue : 1}
        onChange={onAmountChange}
        maxValue={9999999}
      />
      <button
          onClick={saveValue}
          className='button button--big button--big--solid edit-container-item-confirm'>
            {props.itemConfirmLabel}
        </button>
    </>
  );
}
